import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { useContent } from "@/context/ContentContext";
import { useT } from "@/lib/translations";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  PawPrint,
  ChevronLeft,
  AlertTriangle,
  Heart,
  Calendar,
  FileText,
  Shield,
  Download,
  Info,
  Sparkles,
  TrendingUp,
  Users,
  Home,
  Globe,
  Target,
  ArrowRight,
  HeartCrack,
  Activity,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function TransparencyPage() {
  const [impact, setImpact] = useState(null);
  const [lossData, setLossData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMemorial, setSelectedMemorial] = useState(null);
  const { t } = useContent();
  const L = useT();
  const { locale } = useLanguage();

  // Sebep etiketlerinin insan okuyabilir karşılığı (locale-aware).
  const reasonLabel = (r) => ({
    hastalik: L("Hastalık"),
    kaza: L("Kaza"),
    yaslilik: L("Yaşlılık"),
    bilinmiyor: L("Bilinmiyor"),
    kayip_kacis: L("Kaçış"),
    kayip_calinma: L("Çalınma şüphesi"),
    kayip_diger: L("Diğer"),
  }[r] || r || L("Bilinmiyor"));

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/public/impact-report?locale=${locale}`).then((r) => setImpact(r.data)).catch(() => {}),
      axios.get(`${API}/transparency/loss-list`).then((r) => setLossData(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
     
  }, [locale]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!impact) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">{L("Veriler yüklenemedi.")}</p>
      </div>
    );
  }

  const hm = impact.headline_metrics;
  const toc = impact.theory_of_change;
  const stories = impact.stories;
  const trend = impact.monthly_trend.map((m) => ({
    label: m.month.slice(5) + "/" + m.month.slice(2, 4),
    Sahiplendirme: m.adoptions,
    Etkinlik: m.events,
  }));

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/public/impact-report.pdf?locale=${locale}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const stem = locale === "en" ? "kahu-impact-report" : "kahu-etki-raporu";
      a.download = `${stem}-${new Date().toISOString().slice(0, 7)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const kayipRecords = lossData?.loss_records?.filter((r) => r.loss_type === "kayip") || [];
  const vefatRecords = lossData?.loss_records?.filter((r) => r.loss_type === "vefat") || [];

  return (
    <div className="min-h-screen bg-background" data-testid="transparency-page">
      {/* Nav */}
      <PublicNav
        activeKey="seffaflik"
        rightSlot={
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-impact-pdf">
            <Download className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">{L("PDF Rapor")}</span>
          </Button>
        }
      />

      {/* Hero */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-amber-50 via-rose-50/40 to-background border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors text-sm">
            <ChevronLeft className="w-4 h-4" />
            {L("Ana Sayfa")}
          </Link>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full mb-5">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">
              {t("transparency.hero.badge", "Değişim Teorisi (Theory of Change) · IRIS+ Uyumlu · BM SDG Hizalı")}
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl md:text-6xl tracking-tight mb-4">
            {t("transparency.hero.title_prefix", "Şeffaflık ve ")}
            <span className="text-primary">{t("transparency.hero.title_highlight", "Etki")}</span>
            {t("transparency.hero.title_suffix", " Raporu")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
            {t("transparency.hero.description", "Sayılarla değil, hayatlarla. Başardığımız da, henüz yapamadığımız da burada — Pativista'nın şeffaflık ilkesi, KAHU'nun dijital altyapısıyla.")}
          </p>
        </div>
      </section>

      {/* Headline Metrics */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <HeroStat icon={Users} value={hm.lives_in_system} label={t("transparency.headline.lives_in_system", "Sistemdeki Can")} />
            <HeroStat icon={Heart} value={hm.lives_adopted} label={t("transparency.headline.lives_adopted", "Yuva Bulan Can")} accent="rose" />
            <HeroStat icon={Home} value={hm.lives_in_care} label={t("transparency.headline.lives_in_care", "Bakım Altında")} />
            <HeroStat icon={HeartCrack} value={hm.lives_lost ?? 0} label={t("transparency.headline.lives_lost", "Aramızdan Ayrılan")} accent="slate" />
            <HeroStat icon={Calendar} value={hm.events_held_last_year} label={t("transparency.headline.events_held_last_year", "Etkinlik (son 1 yıl)")} />
          </div>
        </div>
      </section>

      {/* Theory of Change */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-3">
            <Target className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <h2 className="font-heading text-2xl sm:text-3xl">{t("transparency.toc.title", "Değişim Teorisi (Theory of Change)")}</h2>
          </div>
          <p className="text-muted-foreground mb-2 max-w-3xl font-medium">
            {L("Kimlikten görünürlüğe, görünürlükten kalıcı yuvaya: KAHU'nun bir canın yaşamında yarattığı değişim zinciri.")}
          </p>
          <p className="text-sm text-muted-foreground mb-8 max-w-3xl">
            {L("KAHU; her canı kimliklendirir, görünür kılar, doğru yuvayla buluşturur ve yaşam yolculuğunu şeffaf biçimde izler. Her aşama bir sonrakinin temelini oluşturur.")}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 md:gap-2">
            <ToCColumn
              title={L("Girdiler")}
              subtitle="Inputs"
              description={L("Değişimi mümkün kılan kaynaklar")}
              color="bg-amber-50 border-amber-200 text-amber-900"
              items={[
                L("KAHU dijital altyapısı"),
                L("Pativista'nın saha deneyimi"),
                L("Veteriner ve gönüllü ağı"),
                L("Bağışçı ve koruyucu aile desteği"),
                L("Güvenilir veri ve süreç standartları"),
              ]}
            />
            <ToCColumn
              title={L("Faaliyetler")}
              subtitle="Activities"
              description={L("Kaynakları eyleme dönüştüren süreçler")}
              color="bg-orange-50 border-orange-200 text-orange-900"
              items={[
                L("Her cana dijital kimlik ve profil oluşturma"),
                L("Sağlık, davranış ve bakım bilgilerini güncelleme"),
                L("Sahiplendirme başvurularını yapılandırılmış biçimde yönetme"),
                L("Bağış ve koruyucu aile eşleşmeleri oluşturma"),
                L("Sahiplendirme sonrası takip ve yaşam güncellemeleri paylaşma"),
              ]}
            />
            <ToCColumn
              title={L("Çıktılar")}
              subtitle="Outputs"
              description={L("Doğrudan ve ölçülebilir üretimler")}
              color="bg-rose-50 border-rose-200 text-rose-900"
              items={[
                L("Kayıtlı ve güncel can profilleri"),
                L("Tamamlanan sahiplendirme başvuruları"),
                L("Gerçekleşen sahiplendirmeler"),
                L("Aktif koruyucu aile ve düzenli destekçiler"),
                L("Paylaşılan yaşam ve sağlık güncellemeleri"),
              ]}
            />
            <ToCColumn
              title={L("Sonuçlar")}
              subtitle="Outcomes"
              description={L("Kısa ve orta vadeli değişim")}
              color="bg-emerald-50 border-emerald-200 text-emerald-900"
              items={[
                L("Canların görünürlüğü ve doğru eşleşme ihtimali artar"),
                L("Sahiplendirme süresi ve iade oranı azalır"),
                L("Personelin manuel takip yükü hafifler"),
                L("Düzenli destek geliri artar"),
                L("Veri güvenilirliği ve kamu güveni güçlenir"),
              ]}
            />
            <ToCColumn
              title={L("Uzun Vadeli Etki")}
              subtitle="Long-term Impact"
              description={L("KAHU'nun katkı sunduğu sistemsel dönüşüm")}
              color="bg-sky-50 border-sky-200 text-sky-900"
              items={[
                L("Daha fazla can güvenli ve kalıcı yuvaya ulaşır"),
                L("Kayıp, ihmal ve açıklanamayan ölüm riski azalır"),
                L("Yaşam alanları finansal olarak daha sürdürülebilir hale gelir"),
                L("Hayvan refahı ölçülebilir ve hesap verebilir olur"),
                L("Model diğer yaşam alanlarına ve belediyelere yayılır"),
              ]}
            />
          </div>
        </div>
      </section>

      {/* PATİVİSTA PİLOTUNUN ANLIK GÖRÜNÜMÜ */}
      <PativistaSnapshot />

      {/* Monthly Trend */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <h2 className="font-heading text-2xl sm:text-3xl">{t("transparency.trend.title", "Aylık Trend")}</h2>
          </div>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            {t("transparency.trend.description", "Son 12 ay — yuva bulan canlar ve yan yana durduğumuz her bir an. Her sütun, sokakta başlayan bir hikâyenin eve dönüşüdür.")}
          </p>
          <Card className="bg-card border border-border/50">
            <CardContent className="p-4 md:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: 12-month summary */}
                <div className="space-y-3 lg:border-r lg:border-border/50 lg:pr-6">
                  {(() => {
                    const totalAdoptions = trend.reduce((s, m) => s + m.Sahiplendirme, 0);
                    const totalEvents = trend.reduce((s, m) => s + m.Etkinlik, 0);
                    const peak = trend.reduce(
                      (acc, m) => (m.Sahiplendirme > acc.value ? { label: m.label, value: m.Sahiplendirme } : acc),
                      { label: "—", value: 0 }
                    );
                    return (
                      <>
                        <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                          <p className="text-xs text-muted-foreground">{L("Son 12 Ay · Sahiplendirme")}</p>
                          <p className="font-heading text-3xl text-primary">{totalAdoptions}</p>
                          <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                            {L("Kuğulu etkinlikleri ve dijital reach üzerinden gerçekleşen tüm yuvaya kavuşmalar.")}
                          </p>
                        </div>
                        <div className="p-4 rounded-xl bg-sky-50 border border-sky-200">
                          <p className="text-xs text-sky-700/80">{L("Son 12 Ay · Etkinlik")}</p>
                          <p className="font-heading text-3xl text-sky-700">{totalEvents}</p>
                          <p className="text-[11px] text-sky-700/70 mt-1 leading-snug">
                            {L("Kuğulu Park ve diğer sahiplendirme etkinliği günleri.")}
                          </p>
                        </div>
                        {peak.value > 0 && (
                          <div className="p-4 rounded-xl bg-background border border-border">
                            <p className="text-xs text-muted-foreground">{L("En Yoğun Ay")}</p>
                            <p className="font-heading text-2xl">{peak.label}</p>
                            <p className="text-[11px] text-muted-foreground mt-1">
                              {L("{n} can yuvasına kavuştu.").replace("{n}", peak.value)}
                            </p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Right: compact chart */}
                <div className="lg:col-span-2">
                  <div className="h-56" style={{ minHeight: 200 }}>
                    <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                      <BarChart data={trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis dataKey="label" fontSize={10} tickLine={false} axisLine={false} interval={0} />
                        <YAxis fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} width={28} />
                        <Tooltip cursor={{ fill: "rgba(217,119,6,0.05)" }} contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} />
                        <Bar dataKey="Sahiplendirme" fill="#d97706" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Etkinlik" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex items-center gap-4 justify-center text-xs text-muted-foreground mt-3">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-primary inline-block" /> {L("Sahiplendirme")}</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-sky-500 inline-block" /> {L("Etkinlik")}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Mini Stories */}
      {(stories.recent_adoptions.length > 0 || stories.recent_events.length > 0) && (
        <section className="py-12 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-2xl sm:text-3xl">{t("transparency.stories.title", "Sayıların Arkasındaki Hikâyeler")}</h2>
            </div>
            <p className="text-muted-foreground mb-8 max-w-2xl">
              {t("transparency.stories.description", "Veri özettir; arkasında her zaman bir yaşanmışlık vardır 🖤")}
            </p>

            {stories.recent_adoptions.length > 0 && (
              <div className="mb-8">
                <h3 className="font-heading text-lg mb-4 text-muted-foreground">{t("transparency.stories.recent_adoptions_label", "Son Yuva Bulan Canlar")}</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stories.recent_adoptions.slice(0, 6).map((d) => (
                    <Link key={d.dog_id} to={`/can/${d.dog_id}`}>
                      <Card className="card-hover overflow-hidden h-full">
                        <div className="flex items-center gap-4 p-4">
                          <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden flex-shrink-0">
                            {d.photo_url ? (
                              <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Heart className="w-6 h-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-heading text-lg">{d.name}</p>
                            <p className="text-xs text-muted-foreground">{d.breed || L("Karışık")}</p>
                            <p className="text-xs text-emerald-600 font-medium mt-1 inline-flex items-center gap-1">
                              <Heart className="w-3 h-3 fill-current" />
                              Yuva buldu
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {stories.recent_events.length > 0 && (
              <div>
                <h3 className="font-heading text-lg mb-4 text-muted-foreground">{t("transparency.stories.recent_events_label", "Son Etkinliklerden")}</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {stories.recent_events.map((e) => (
                    <Link key={e.event_id} to={`/etkinlikler/${e.event_id}`}>
                      <Card className="card-hover overflow-hidden h-full">
                        <CardContent className="p-5">
                          <p className="text-xs text-muted-foreground mb-1">
                            {new Date(e.event_date).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                          </p>
                          <p className="font-heading text-base mb-2">{e.location_name}</p>
                          {e.recap?.adoptions_count > 0 && (
                            <p className="text-sm text-rose-600 inline-flex items-center gap-1">
                              <Heart className="w-3.5 h-3.5 fill-current" />
                              <strong>{e.recap.adoptions_count}</strong> can yuva buldu
                            </p>
                          )}
                          {e.recap?.visitors_estimated > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              ~{e.recap.visitors_estimated} ziyaretçi
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* SDG Alignment */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-3">
            <Globe className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <h2 className="font-heading text-2xl sm:text-3xl">{t("transparency.sdg.title", "BM Sürdürülebilir Kalkınma Hedefleri")}</h2>
          </div>
          <p className="text-muted-foreground mb-8 max-w-2xl">
            {t("transparency.sdg.description", "Bir sokak hayvanına uzanan el, aslında bir şehrin kaderine dokunur. KAHU'nun yaptığı iş; halk sağlığından kentsel yaşam kalitesine, biyolojik çeşitlilikten toplumsal vicdana kadar uzanan küresel bir zincirin parçasıdır.")}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {toc.sdg_alignment.map((sdg) => (
              <Card key={sdg.sdg} className="overflow-hidden">
                <div className="h-2" style={{ backgroundColor: sdg.icon_color }} />
                <CardContent className="p-5">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-heading text-3xl" style={{ color: sdg.icon_color }}>
                      {sdg.sdg}
                    </span>
                    <span className="font-semibold text-sm">{sdg.title}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{sdg.rationale}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Loss List (kept as a section — also a form of transparency) */}
      {(kayipRecords.length > 0 || vefatRecords.length > 0) && (
        <section className="py-12 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-2xl sm:text-3xl">{t("transparency.losses.title", "Kaybettiklerimiz")}</h2>
            </div>
            <p className="text-muted-foreground mb-8 max-w-2xl">
              {t("transparency.losses.description", "Kayıplarımızı saklamıyoruz — her can bir hikâyedir, hatırlanmaya değer.")}
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <Card className="bg-card border border-border/50">
                <CardContent className="p-5">
                  <p className="text-sm text-muted-foreground">{L("Toplam Kayıt")}</p>
                  <p className="font-heading text-2xl mt-1">{lossData?.summary?.total || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-card border border-border/50">
                <CardContent className="p-5">
                  <p className="text-sm text-muted-foreground">{L("Kayıp")}</p>
                  <p className="font-heading text-2xl mt-1 text-amber-600">{lossData?.summary?.total_kayip || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-card border border-border/50">
                <CardContent className="p-5">
                  <p className="text-sm text-muted-foreground">{L("Vefat")}</p>
                  <p className="font-heading text-2xl mt-1 text-slate-600">{lossData?.summary?.total_vefat || 0}</p>
                </CardContent>
              </Card>
            </div>

            {[...kayipRecords, ...vefatRecords].slice(0, 10).length > 0 && (
              <Card className="bg-card border border-border/50">
                <CardContent className="p-5">
                  <div className="space-y-3">
                    {[...kayipRecords, ...vefatRecords].slice(0, 10).map((r, i) => (
                      <button
                        key={r.loss_id || i}
                        onClick={() => setSelectedMemorial(r)}
                        className="w-full text-left flex items-center justify-between gap-3 pb-3 border-b last:border-0 last:pb-0 hover:bg-muted/40 rounded-lg px-2 -mx-2 py-1.5 transition-colors group focus:outline-none focus:ring-2 focus:ring-primary/40"
                        data-testid={`memorial-item-${r.loss_id || i}`}
                        aria-label={`${r.dog_name} — hikayeyi oku`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate group-hover:text-primary transition-colors">{r.dog_name || "—"}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {r.loss_date ? new Date(r.loss_date).toLocaleDateString("tr-TR") : (r.created_at ? new Date(r.created_at).toLocaleDateString("tr-TR") : "")}
                            {r.loss_reason ? ` · ${reasonLabel(r.loss_reason)}` : ""}
                            {r.story ? ` · ${r.story.slice(0, 60)}${r.story.length > 60 ? "…" : ""}` : ""}
                          </p>
                        </div>
                        <Badge
                          className={
                            r.loss_type === "kayip"
                              ? "bg-amber-100 text-amber-700 border border-amber-200 shrink-0"
                              : "bg-slate-100 text-slate-700 border border-slate-200 shrink-0"
                          }
                        >
                          {r.loss_type === "kayip" ? L("Kayıp") : L("Vefat")}
                        </Badge>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground/70 mt-4 text-center">
                    {L("Her isme dokun; hikayesini oku, hatırla.")}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Methodology + PDF download */}
      <section className="py-12 bg-card border-t border-border/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-3">
            <FileText className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <h2 className="font-heading text-2xl">{t("transparency.methodology.title", "Metodoloji")}</h2>
          </div>
          <p className="text-muted-foreground mb-6 max-w-3xl leading-relaxed">
            {t("transparency.methodology.intro", "KAHU'nun her metriği; sokakta başlayıp eve uzanan dört aşamalı yaşam döngüsünden doğar: Dijital Kimlik → Can Profili → Sahiplendirme Motoru → Yaşam Kanıtı. Aşağıda bu döngünün verisini nasıl okuduğumuzu, hangi kavramları nasıl tanımladığımızı ve hangi uluslararası çerçevelerle hizalandığımızı bulabilirsiniz.")}
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 mb-8">
            {impact.methodology_notes.map((n, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>{n}</span>
              </li>
            ))}
          </ul>
          <div className="bg-muted/50 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-heading text-lg mb-1">{t("transparency.pdf.title", "Etki raporunu indir")}</p>
              <p className="text-sm text-muted-foreground">
                {t("transparency.pdf.description", "Dört aşamalı yaşam döngüsünü, Değişim Teorisi (Theory of Change) yapısını ve IRIS+ / BM SDG hizalamasını paydaşlarınızla paylaşılabilecek tek belgede topladık.")}
              </p>
            </div>
            <Button onClick={downloadPdf} className="btn-pill bg-primary text-primary-foreground shadow-lg shadow-primary/20 transition-all duration-200 hover:bg-primary hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30" data-testid="footer-download-pdf">
              <Download className="w-4 h-4 mr-2" />
              {t("transparency.pdf.button", "PDF Olarak İndir")}
            </Button>
          </div>
        </div>
      </section>

      <footer className="py-10 bg-background border-t border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center text-sm text-muted-foreground">
          <p>
            Framework: <strong className="text-foreground">{impact.framework.name}</strong> · {impact.framework.version} · {impact.framework.publisher_label || L("Yayıncı")}: {impact.framework.publisher}
          </p>
          <p className="mt-1 text-xs">
            {impact.framework.footer_note || L("Rapor canlı verilerden anlık üretilir")} · {new Date(impact.generated_at).toLocaleString(locale === "en" ? "en-US" : "tr-TR")}
          </p>
        </div>
      </footer>

      {/* Kamu Anıt Modalı — bir cana tıklayınca hikayesi açılır (edit YOK, saygılı sunum) */}
      <Dialog open={!!selectedMemorial} onOpenChange={(v) => !v && setSelectedMemorial(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="memorial-detail-dialog">
          {selectedMemorial && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <DialogTitle className="text-2xl font-heading">
                    {selectedMemorial.dog_name || "—"}
                  </DialogTitle>
                  {selectedMemorial.loss_type === "kayip" ? (
                    <Badge className="bg-amber-100 text-amber-700 border border-amber-200">{L("Kayıp")}</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-700 border border-slate-200">Vefat</Badge>
                  )}
                </div>
                <DialogDescription>
                  {selectedMemorial.loss_date && (
                    <>
                      {new Date(selectedMemorial.loss_date).toLocaleDateString("tr-TR", {
                        day: "numeric", month: "long", year: "numeric",
                      })}
                    </>
                  )}
                  {selectedMemorial.loss_reason && (
                    <>
                      {selectedMemorial.loss_date ? " · " : ""}
                      <span className="text-foreground/70">{reasonLabel(selectedMemorial.loss_reason)}</span>
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {/* Hikaye — ana içerik */}
                {selectedMemorial.story && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-medium">
                      Hikayesi
                    </p>
                    <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed bg-muted/30 rounded-lg p-4 border border-border/50 italic">
                      {selectedMemorial.story}
                    </p>
                  </div>
                )}

                {/* Kayıp durumları için son görülme bilgisi */}
                {selectedMemorial.loss_type === "kayip" && (selectedMemorial.last_seen_location || selectedMemorial.last_seen_date) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-amber-50/50 rounded-lg p-3 border border-amber-100">
                    {selectedMemorial.last_seen_location && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-amber-700/80 mb-1 font-medium">
                          Son Görülme Yeri
                        </p>
                        <p className="text-sm text-foreground/90">{selectedMemorial.last_seen_location}</p>
                      </div>
                    )}
                    {selectedMemorial.last_seen_date && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-amber-700/80 mb-1 font-medium">
                          Son Görülme Tarihi
                        </p>
                        <p className="text-sm text-foreground/90">
                          {new Date(selectedMemorial.last_seen_date).toLocaleDateString("tr-TR")}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Cins bilgisi — varsa göster (public bilgi) */}
                {selectedMemorial.dog_breed && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <PawPrint className="w-3.5 h-3.5" />
                    <span>{selectedMemorial.dog_breed}</span>
                  </div>
                )}

                {/* Duygusal alt satır + profil linki */}
                <div className="pt-3 border-t border-border/50">
                  {selectedMemorial.loss_type === "vefat" ? (
                    <p className="text-xs text-muted-foreground italic">
                      Her can bir hikayedir. {selectedMemorial.dog_name}&apos;i anıyor, yaşadığı iyilikleri hatırlıyoruz.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      {selectedMemorial.dog_name}&apos;i arıyoruz. Görürseniz lütfen bize haber verin.
                    </p>
                  )}
                  {selectedMemorial.dog_id && (
                    <Link
                      to={`/can/${selectedMemorial.dog_id}`}
                      className="text-primary hover:underline flex items-center gap-1 mt-3 text-sm"
                      data-testid="memorial-view-profile-link"
                    >
                      <PawPrint className="w-3.5 h-3.5" />
                      {selectedMemorial.dog_name} profiline git →
                    </Link>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HeroStat({ icon: Icon, value, label, accent = "amber" }) {
  const colors = {
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <Card className="overflow-hidden bg-card border border-border/50">
      <CardContent className="p-5 md:p-6">
        <div className={`w-10 h-10 rounded-xl ${colors[accent]} flex items-center justify-center mb-3`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <p className="font-heading text-3xl md:text-4xl text-foreground">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </CardContent>
    </Card>
  );
}

function ToCColumn({ title, subtitle, description, items, color }) {
  return (
    <div className={`rounded-2xl border-2 ${color} p-4 flex flex-col`}>
      <div className="mb-3">
        <p className="font-heading text-base font-semibold">{title}</p>
        <p className="text-[10px] uppercase tracking-wider opacity-60">{subtitle}</p>
        {description && (
          <p className="text-xs opacity-75 mt-1.5 italic leading-snug">{description}</p>
        )}
      </div>
      <ul className="space-y-2 text-xs leading-relaxed flex-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <span className="opacity-50 flex-shrink-0">▸</span>
            <span className="flex-1">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============ PATİVİSTA SNAPSHOT ============
function PativistaSnapshot() {
  const L = useT();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/transparency/snapshot`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center text-muted-foreground">
          Anlık görünüm yükleniyor…
        </div>
      </section>
    );
  }

  const otherLabels = {
    rezerve: L("Rezerve"),
    transfer: L("Transfer Edildi"),
    kayip: L("Kayıp"),
    vefat: L("Vefat"),
    iade: L("İade"),
    diger: L("Diğer"),
  };

  return (
    <section className="py-12 bg-card">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-3 mb-3">
          <Activity className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <h2 className="font-heading text-2xl sm:text-3xl">{L("Pativista Pilotunun Anlık Görünümü")}</h2>
        </div>
        <p className="text-muted-foreground mb-2 max-w-3xl">
          {L("KAHU'nun değişim teorisinin sahadaki güncel karşılığı.")}
        </p>
        <p className="text-xs text-muted-foreground mb-8 max-w-3xl">
          {L("Her gösterge canlı, otomatik ve Pativista'nın saha kayıtlarından beslenir. Manuel veri girişi yapılmaz — sayılar Pativista admin panelindeki her aksiyonla anında yenilenir.")}
        </p>

        {data.data_inconsistency && (
          <Alert className="mb-6 border-amber-400 bg-amber-50">
            <AlertDescription className="text-amber-900 text-sm">
              {L("⚠️ Veri tutarsızlığı: kayıtlı toplam can ({total}) ile statülerin toplamı ({computed}) eşleşmiyor.").replace("{total}", data.total_registered).replace("{computed}", data.computed_total)}
            </AlertDescription>
          </Alert>
        )}

        <h3 className="font-heading text-xl text-foreground mb-4">{L("Ana Etki Göstergeleri")}</h3>

        {/* Status breakdown */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <IndicatorMetric
            label={L("Sistemde Kayıtlı")}
            value={data.total_registered}
            note={L("KAHU'da dijital kimlik almış can sayısı.")}
            highlight
          />
          <IndicatorMetric
            label={L("Aktif Bakım Altında")}
            value={data.status_breakdown.active_care}
            note={L("Alanda, geçici bakımda, sahiplenmeye hazır ya da tedavi gören canlar.")}
          />
          <IndicatorMetric
            label={L("Kalıcı Yuvaya Kavuşan")}
            value={data.status_breakdown.permanent_home}
            note={L("Sahiplendirilmiş ve yeni ailesiyle yaşamaya başlamış canlar.")}
          />
          <Popover>
            <PopoverTrigger asChild>
              <div
                className="text-left p-5 rounded-2xl bg-background border border-border hover:bg-muted/50 transition-all cursor-help"
                data-testid="snapshot-other-toggle"
              >
                <p className="text-xs text-muted-foreground">{L("Diğer Statüler")}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="font-heading text-3xl">{data.status_breakdown.other}</p>
                  <span className="text-xs text-muted-foreground">ⓘ</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{L("Vefat, kayıp, transfer ve diğer durumlar — detay için hover.")}</p>
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-4" align="end">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">{L("Statü Dağılımı")}</p>
              <div className="space-y-2 text-sm">
                {Object.entries(otherLabels)
                  .filter(([key]) => (data.other_detail[key] || 0) > 0)
                  .map(([key, label]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-medium">{data.other_detail[key]}</span>
                    </div>
                  ))}
                {Object.values(data.other_detail).filter((v) => v > 0).length === 0 && (
                  <p className="text-muted-foreground text-xs">{L("Bu kategoride henüz kayıt yok.")}</p>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Operational signals */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <IndicatorMetric
            label={L("Aktif Koruyucu Aile")}
            value={data.guardians_active}
            note={L("Bir canı düzenli olarak destekleyen onaylı destekçi sayısı.")}
          />
          <IndicatorMetric
            label={L("Son 30 Gün Yaşam Güncellemesi")}
            value={data.life_updates_30d}
            note={L("Pativista'nın canların profillerine düştüğü güncel sağlık, davranış ve yaşam notları.")}
          />
          <IndicatorMetric
            label={L("Tamamlanan Sahiplendirme Başvurusu")}
            value={data.completed_applications}
            note={L("Onaylanmış ve süreci sonuçlanmış başvuru sayısı.")}
          />
        </div>
      </div>
    </section>
  );
}

function IndicatorMetric({ label, value, note, highlight }) {
  return (
    <div className={`p-5 rounded-2xl border ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-background"}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-heading text-3xl mt-1">{value}</p>
      {note && <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{note}</p>}
    </div>
  );
}

function SnapshotStat({ label, value, highlight }) {
  return (
    <div className={`p-5 rounded-2xl border ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-background"}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-heading text-3xl mt-1">{value}</p>
    </div>
  );
}

function IndicatorCard({ indicator }) {
  const L = useT();
  const { label, numerator, denominator, percent, period, note, median_days, currency } = indicator;
  const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    if (currency === "TRY") return new Intl.NumberFormat("tr-TR").format(v) + " ₺";
    return v;
  };
  return (
    <div className="p-5 rounded-2xl bg-background border border-border">
      <p className="text-sm text-muted-foreground mb-2">{label}</p>
      <div className="flex items-baseline gap-2 mb-1">
        {median_days !== undefined && median_days === null ? (
          <p className="font-heading text-2xl">—</p>
        ) : median_days !== undefined ? (
          <>
            <p className="font-heading text-3xl">{median_days}</p>
            <span className="text-sm text-muted-foreground">{L("gün (medyan)")}</span>
          </>
        ) : (
          <>
            <p className="font-heading text-3xl">
              {fmt(numerator)} <span className="text-muted-foreground text-xl">/ {fmt(denominator)}</span>
            </p>
            {percent !== null && (
              <span className="text-base text-primary font-medium">— %{percent}</span>
            )}
          </>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Ölçüm dönemi: {period}</p>
      {note && (
        <p className="text-xs text-amber-700 mt-2 italic">{note}</p>
      )}
    </div>
  );
}
